import { mdiAccountCircleOutline, mdiDownload, mdiFileChartOutline } from '@mdi/js';
import i18n from '@/i18n';
import { ACCESS_KEYS } from '@/config/entitlementKeys';
import { FEATURE_FLAGS } from '@/utils/featureFlags';

export default [
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('mijn_omgeving.title'),
      icon: mdiAccountCircleOutline,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      breadcrumbs: [
        { label: i18n.global.t('mijn_omgeving.title'), to: '/my-environment/overview' },
      ],
    },
    path: '/my-environment',
    component: () => import('@/pages/my-environment/MyEnvironment.vue'),
    redirect: { name: 'my-environment.overview' },
    // Each non-overview child passes `embedded: false` so the section
    // omits its own card-level title — the page-level header (resolved by
    // `MyEnvironment.vue` from `route.meta.titleKey/subtitleKey` below)
    // is the single source of identity for these standalone pages.
    children: [
      {
        path: 'overview',
        name: 'my-environment.overview',
        meta: {
          // PI-322: the Overzicht subpage title drops "Mijn" (the sidebar +
          // breadcrumb root stay "Mijn omgeving"). Reuse the already-localized
          // sub-nav label rather than minting a parallel key.
          titleKey: 'mijn_omgeving.sections.overzicht',
          subtitleKey: 'mijn_omgeving.subtitle',
        },
        component: () =>
          import(
            '@/pages/my-environment/components/OverviewSection.vue'
          ),
      },
      {
        path: 'dashboards',
        name: 'my-environment.dashboards',
        meta: {
          titleKey: 'mijn_omgeving.dashboards.title',
          subtitleKey: 'mijn_omgeving.dashboards.subtitle',
        },
        props: { embedded: false },
        component: () =>
          import(
            '@/pages/my-environment/components/MyDashboardsSection.vue'
          ),
      },
      {
        path: 'kpis',
        name: 'my-environment.kpis',
        meta: {
          titleKey: 'mijn_omgeving.kpis.title',
          subtitleKey: 'mijn_omgeving.kpis.subtitle',
        },
        props: { embedded: false },
        component: () =>
          import(
            '@/pages/my-environment/components/MyKpisSection.vue'
          ),
      },
      {
        path: 'charts',
        name: 'my-environment.charts',
        meta: {
          titleKey: 'mijn_omgeving.charts.title',
          subtitleKey: 'mijn_omgeving.charts.subtitle',
        },
        props: { embedded: false },
        component: () =>
          import(
            '@/pages/my-environment/components/MyChartsSection.vue'
          ),
      },
      {
        path: 'toolruns',
        name: 'my-environment.toolruns',
        meta: {
          titleKey: 'mijn_omgeving.toolruns.title',
          subtitleKey: 'mijn_omgeving.toolruns.subtitle',
        },
        props: { embedded: false },
        component: () =>
          import(
            '@/pages/my-environment/components/MyToolrunsSection.vue'
          ),
      },
      {
        path: 'profiles',
        name: 'my-environment.profiles',
        meta: {
          titleKey: 'profiles.title',
          subtitleKey: 'profiles.subtitle',
        },
        props: { embedded: false },
        component: () => import('@/features/profiles/pages/ProfilesPage.vue'),
      },
      {
        path: 'assets',
        name: 'my-environment.assets',
        meta: {
          titleKey: 'mijn_omgeving.assets.title',
          subtitleKey: 'mijn_omgeving.assets.subtitle',
          requiresGlobalFeatureFlag: FEATURE_FLAGS.ENVIRONMENT_ASSETS,
        },
        props: { embedded: false },
        component: () =>
          import(
            '@/pages/my-environment/components/MyAssetsSection.vue'
          ),
      },
    ],
  },
  // Alerts index is a top-level route (not nested under MyEnvironment) so the
  // alerts-ship (@stoep) UI can mount its own StandardToolPage chrome — in-page
  // header + action buttons + the shared tool layout — matching the other
  // redesign tool pages. Still lives at /my-environment/alerts in the
  // Personalisation cauldron; the cauldron sidebar links here via /alerts.
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('mijn_omgeving.alerts.title'),
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      // PI-322: drop the trailing "Mijn alerts" breadcrumb so Alerts matches
      // the other sub-pages (which only carry the "Mijn omgeving" root).
      breadcrumbs: [
        { label: i18n.global.t('mijn_omgeving.title'), to: '/my-environment/overview' },
      ],
    },
    path: '/my-environment/alerts',
    name: 'my-environment.alerts',
    component: () => import('@/pages/alerts/AlertIndex.vue'),
  },
  // Reports — user-owned battery-benchmark reports. Standalone (like Alerts)
  // so the feature mounts its own StandardToolPage chrome. Lives in the
  // Personalisation cauldron ("Mijn omgeving") since a report is a thing the
  // user creates and owns, not a live market tool.
  //
  // Reports has its own permission; feedback Shadow Trading does not include it.
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('page_titles.reports'),
      icon: mdiFileChartOutline,
      requiresAuth: true,
      requiresFeatureFlag: 'report-builder',
      requiresEntitlement: ACCESS_KEYS.TOOL_REPORTS,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: { kind: 'page', id: 'page:reports', label: i18n.global.t('page_titles.reports') },
      breadcrumbs: [
        { label: i18n.global.t('mijn_omgeving.title'), to: '/my-environment/overview' },
      ],
    },
    path: '/my-environment/reports',
    name: 'my-environment.reports',
    component: () => import('@/features/reports/pages/ReportsDashboardPage.vue'),
  },
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('export_jobs.page_title'),
      icon: mdiDownload,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
      breadcrumbs: [
        { label: i18n.global.t('mijn_omgeving.title'), to: '/my-environment/overview' },
      ],
    },
    path: '/my-environment/exports',
    name: 'export-jobs',
    component: () => import('@/pages/my-environment/ExportJobs.vue'),
  },
  // Asset detail is a top-level route (not nested under MyEnvironment) so it
  // can mount its own StandardToolPage chrome with a dynamic title (asset
  // name) instead of inheriting the meta-key based header from MyEnvironment.
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('mijn_omgeving.assets.detail.title'),
      requiresGlobalFeatureFlag: FEATURE_FLAGS.ENVIRONMENT_ASSETS,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      breadcrumbs: [
        { label: i18n.global.t('mijn_omgeving.title'), to: '/my-environment/overview' },
        { label: i18n.global.t('mijn_omgeving.assets.title'), to: '/my-environment/assets' },
      ],
    },
    path: '/my-environment/assets/:assetId',
    name: 'my-environment.asset-detail',
    component: () =>
      import('@/pages/asset-detail/AssetDetail.vue'),
  },
  // Backwards-compatible redirects from the old Dutch URLs.
  { path: '/mijn-omgeving', redirect: '/my-environment' },
  { path: '/mijn-omgeving/overzicht', redirect: '/my-environment/overview' },
  { path: '/mijn-omgeving/dashboards', redirect: '/my-environment/dashboards' },
  { path: '/mijn-omgeving/kpis', redirect: '/my-environment/kpis' },
  { path: '/mijn-omgeving/grafieken', redirect: '/my-environment/charts' },
  { path: '/mijn-omgeving/alerts', redirect: '/my-environment/alerts' },
  { path: '/mijn-omgeving/toolruns', redirect: '/my-environment/toolruns' },
  { path: '/mijn-omgeving/exports', redirect: '/my-environment/exports' },
  { path: '/my-environment/products', redirect: '/platform/subscription/entitlements' },
  { path: '/mijn-omgeving/producten', redirect: '/platform/subscription/entitlements' },
  // Reports moved out of the tools cauldron into Mijn omgeving.
  { path: '/tools/reports', redirect: '/my-environment/reports' },
];
